import React, { FunctionComponent, ChangeEvent, FocusEvent, KeyboardEvent, useState, useEffect } from 'react';
import clsx from 'clsx';
import { FieldError } from '@spike/model';
import { Label } from '../Label/Label';
import styles from './NumberField.module.scss';
import padStart from 'lodash/padStart';

export interface NumberFieldProps {
    id?: string;
    name?: string;
    label?: string | JSX.Element;
    placeholder?: string;
    value: number | undefined;
    required?: boolean;
    errors?: Array<FieldError>;
    onBlur?: () => void;
    onChange: (value: number | undefined, name?: any) => void;
    onFocus?: () => void;
    className?: string;
    autoFocus?: boolean;
    autoComplete?: 'off' | 'on';
    endIcon?: JSX.Element;
    startIcon?: JSX.Element;
    multiline?: boolean;
    onEnterPressed?: () => void;
    disabled?: boolean;
    scale?: number;
    precision?: number;
    alignRight?: boolean;
    information?: string;
    labelClassName?: string;
    max?: number;
    allowEmptyValue?: boolean;
    allowNegative?: boolean;
    default?: string;
    onKeyPress?: (event: KeyboardEvent) => void;
    removeTrailingZeros?: boolean;
}

export const NumberField: FunctionComponent<NumberFieldProps> = ({
    id,
    name,
    label,
    placeholder,
    value,
    required,
    errors,
    onBlur,
    onChange,
    onFocus,
    className,
    autoFocus = false,
    autoComplete = 'off',
    endIcon,
    startIcon,
    onEnterPressed,
    disabled,
    scale,
    precision,
    alignRight,
    max,
    allowEmptyValue,
    allowNegative = false,
    default: defaultValue,
    onKeyPress,
    removeTrailingZeros = false,
    ...props
}) => {
    // Helper function to remove trailing zeros after decimal point
    const trimTrailingZeros = (numStr: string): string => {
        if (!removeTrailingZeros || !numStr.includes('.')) return numStr;

        // Remove trailing zeros after decimal point
        let result = numStr.replace(/(\.\d*?)0+$/, '$1');

        // If only decimal point remains, remove it too
        if (result.endsWith('.')) {
            result = result.slice(0, -1);
        }

        return result || '0';
    };
    const [text, setText] = useState(() => {
        if (defaultValue) return defaultValue;

        // FIX: Properly format initial value with scale
        if (value !== undefined && scale !== undefined && typeof value === 'number' && !isNaN(value)) {
            const formatted = value.toFixed(scale);
            return trimTrailingZeros(formatted);
        }

        return value?.toString() || '';
    });
    const [isFocused, setIsFocused] = useState(false);

    const commitText = (nextText: string) => {
        if (nextText.length === 0 || nextText === '-') {
            onChange(undefined, name);
            return;
        }

        const numericValue = Number(nextText);
        onChange(Number.isNaN(numericValue) ? undefined : numericValue, name);
    };

    const error = errors?.find(error => error.fieldName === name);
    const hasError = Boolean(error);

    // Sync with external value changes (but don't override user input)
    useEffect(() => {
        const externalValue = value?.toString() || '';

        // FIX: Allow synchronization for scale fields when not focused
        if (!isFocused && externalValue !== text) {
            if (scale === undefined) {
                setText(externalValue);
            } else {
                // For scale fields, format properly
                const numValue = value;
                if (numValue !== undefined && typeof numValue === 'number' && !isNaN(numValue)) {
                    const formatted = numValue.toFixed(scale);
                    setText(trimTrailingZeros(formatted));
                } else {
                    setText('');
                }
            }
        }
    }, [value, isFocused, scale]);

    // Handle default value
    useEffect(() => {
        if (defaultValue && defaultValue !== text) {
            setText(defaultValue);
        }
    }, [defaultValue]);

    const formatText = (rawValue: string): string => {
        if (allowNegative && scale === undefined) {
            const isNegative = rawValue.trim().startsWith('-');
            const digits = rawValue.replace(/\D/g, '').replace(/^0+/, '');

            if (digits.length === 0) {
                if (isNegative) {
                    return '-';
                }
                return allowEmptyValue ? '' : '0';
            }

            return isNegative ? `-${digits}` : digits;
        }

        const cleanValue = rawValue.replace(/\D/g, '').replace(/^0+/, '');

        if (cleanValue.length === 0) {
            if (allowEmptyValue) {
                return '';
            } else if (scale !== undefined && scale === 0) {
                return '0';
            } else if (scale !== undefined) {
                return `0.${'0'.repeat(scale)}`;
            } else {
                return '0';
            }
        }

        if (scale) {
            // Better logic for formatting with scale
            // If we have fewer digits than scale, don't assume they're all decimals
            if (cleanValue.length <= scale && cleanValue.length <= 2) {
                // Only for very small values (1-2 digits), treat as decimals
                return `0.${padStart(cleanValue, scale, '0')}`;
            } else {
                // For larger values, treat normally
                const integerPart = cleanValue.slice(0, Math.max(cleanValue.length - scale, 0));
                const decimalPart = cleanValue.slice(Math.max(cleanValue.length - scale, 0));
                return `${integerPart || '0'}.${padStart(decimalPart, scale, '0')}`;
            }
        } else {
            return cleanValue;
        }
    };

    const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.value;

        if (allowNegative && scale === undefined) {
            if (newValue !== '' && !/^-?\d*$/.test(newValue)) {
                return;
            }

            const digits = newValue.replace(/\D/g, '');
            if (precision && digits.length > precision) {
                return;
            }

            const formattedText = formatText(newValue);
            setText(formattedText);
            commitText(formattedText);
            return;
        }

        const cleanValue = newValue.replace(/\D/g, '').replace(/^0+/, '');

        // Validate input based on precision
        if (precision && cleanValue.length > precision) {
            return; // Don't update if exceeds precision
        }

        // Allow the change if it's valid
        if (newValue.length === 0 || Number(newValue) === 0 || (newValue.length > 0 && cleanValue.length > 0)) {
            const formattedText = formatText(newValue);
            setText(formattedText);
            commitText(formattedText);
        }
    };

    const onFocusHandler = (event: FocusEvent<HTMLInputElement>): void => {
        setIsFocused(true);
        event.target.selectionStart = 0;
        event.target.selectionEnd = event.target.value.length;
        onFocus?.();
    };

    const onBlurHandler = (): void => {
        setIsFocused(false);

        // FIX: Better blur handling
        if (scale !== undefined) {
            // If the text already has decimal formatting, preserve it
            if (text.includes('.')) {
                const parts = text.split('.');
                if (parts.length === 2) {
                    const intPart = parts[0].replace(/\D/g, '') || '0';
                    const decPart = parts[1].replace(/\D/g, '').substring(0, scale);
                    const formatted = trimTrailingZeros(`${intPart}.${padStart(decPart, scale, '0')}`);
                    setText(formatted);
                    commitText(formatted);
                    onBlur?.();
                    return;
                }
            }

            // Only reformat if no decimal point exists
            const clean = text.replace(/\D/g, '');
            const formatted = trimTrailingZeros(formatText(clean));
            setText(formatted);
            commitText(formatted);
        }

        onBlur?.();
    };

    const keyPressHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && onEnterPressed) {
            onEnterPressed();
        } else {
            onKeyPress?.(event);
        }
    };

    const hasNonZeroValue = () => {
        const numericValue = Number(text.replace(/[^\d.]/g, ''));
        return !isNaN(numericValue) && numericValue > 0;
    };

    const inputClasses = clsx(styles.input, className, {
        [styles.inputError]: hasError,
        [styles.inputFilled]: text.length > 0,
        [styles.hasValue]: hasNonZeroValue(),
        [styles.withStartIcon]: startIcon,
        [styles.withEndIcon]: endIcon,
        [styles.inputTextRight]: alignRight,
        [styles.disabled]: disabled,
        [styles.notAutoFocus]: autoFocus === false
    });

    const wrapperClasses = clsx(styles.inputWrapper, {
        [styles.inputWrapperWithStartIcon]: startIcon,
        [styles.inputWrapperWithEndIcon]: endIcon,
        [styles.focused]: isFocused,
        [styles.hasValue]: hasNonZeroValue()
    });

    return (
        <div className={clsx(styles.root, { [styles.focused]: isFocused })}>
            {label && (
                <Label
                    required={required}
                    error={error?.errorMessage || (hasError ? 'Error' : undefined)}
                    htmlFor={id}
                >
                    {label}
                </Label>
            )}
            <div className={wrapperClasses}>
                {startIcon && <span className={styles.icon}>{startIcon}</span>}
                <input
                    {...props}
                    id={id}
                    name={name}
                    type="text"
                    placeholder={placeholder}
                    value={text}
                    onBlur={onBlurHandler}
                    onChange={changeHandler}
                    onFocus={onFocusHandler}
                    autoFocus={autoFocus}
                    autoComplete={autoComplete}
                    className={inputClasses}
                    onKeyDown={keyPressHandler}
                    disabled={disabled}
                    max={max}
                />
                {endIcon && <span className={styles.icon}>{endIcon}</span>}
            </div>
        </div>
    );
};

export default NumberField;
